/* =========================================================
   HORTIGO OS V92.2 — assets/hg-core.js
   CORE (shared): boot, api, config normalize, credits/tokens,
   vouchers, prompt runner, sanitize helpers, events + legacy aliases.

   Exposes: window.HG = {
     env, state,
     ensureConfig(), getCosts(), loadServerTokens(),
     ensureWelcomeGrantServerSynced(), consume(cost), redeem(code),
     runPrompt({prompt,type,file}), runAI({prompt,type,file}) [alias],
     buildPromptMarket(), buildPromptChat(), buildPromptDoctor() [helpers],
     navTo(viewKey),
     ui: { escapeHTML, setOutLoading, applyOutputReadability,
           decodeIfEscaped, stripCSSLikeText, extractHtmlOnly,
           sanitizeToSafeHTML, sanitizeAIHtml [alias] }
   }

   Events:
   - "hg:ready" (HG published)
   - "hg:config-updated" (detail.cfg)
   - "hg:tokens-updated" (detail.tokens)
========================================================= */

(() => {
  "use strict";

  // ---------- FIND ROOT ----------
  function findRoot() {
    return document.getElementById("hg-root");
  }

  function parseBoot(root) {
    try {
      const raw = root.getAttribute("data-hg-boot") || "{}";
      // getAttribute returns decoded entities; JSON.parse is ok
      return JSON.parse(raw);
    } catch (e) {
      console.error("[HG CORE] boot JSON parse failed:", e);
      return {};
    }
  }

  // ---------- HELPERS ----------
  function clone(o) {
    try { return structuredClone(o); } catch (_) { return JSON.parse(JSON.stringify(o)); }
  }

  function escapeHTML(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function normalizeCode(c) {
    return String(c || "").trim().toUpperCase();
  }

  // ---------- DEFAULT CFG ----------
  const DEFAULT_CFG = {
    costs: { basic: 1, std: 3, prem: 5 },
    photoExtra: 2,
    packages: [
      { credits: 5, text: "10 RON" },
      { credits: 20, text: "35 RON" },
      { credits: 50, text: "75 RON" },
      { credits: 0, text: "Custom" }
    ],
    crops: [],
    vouchers: []
  };

  // Accept BOTH schemas:
  // - new: costs.basic/std/prem
  // - old: costs.chat/image/sim/market OR per-module naming
  function normalizeConfig(rawCfg) {
    const cfg = Object.assign(clone(DEFAULT_CFG), rawCfg || {});
    cfg.costs = cfg.costs || {};
    const c = cfg.costs || {};

    const hasNew = (c.basic != null || c.std != null || c.prem != null);

    if (!hasNew) {
      const basic =
        Number.isFinite(+c.chat) ? +c.chat :
        Number.isFinite(+c.market) ? +c.market :
        1;

      const std =
        Number.isFinite(+c.std) ? +c.std :
        Number.isFinite(+c.sim) ? +c.sim :
        3;

      const prem =
        Number.isFinite(+c.prem) ? +c.prem :
        Number.isFinite(+c.image) ? +c.image :
        5;

      cfg.costs = { basic, std, prem };
    } else {
      cfg.costs = {
        basic: Number.isFinite(+c.basic) ? +c.basic : 1,
        std:   Number.isFinite(+c.std)   ? +c.std   : 3,
        prem:  Number.isFinite(+c.prem)  ? +c.prem  : 5
      };
    }

    cfg.photoExtra = Number.isFinite(+cfg.photoExtra) ? +cfg.photoExtra : 2;

    cfg.packages = Array.isArray(cfg.packages) ? cfg.packages : clone(DEFAULT_CFG.packages);
    cfg.packages = cfg.packages.map((p) => ({
      credits: Number.isFinite(+p?.credits) ? +p.credits : 0,
      text: String(p?.text || "")
    }));

    cfg.crops = Array.isArray(cfg.crops) ? cfg.crops : [];
    cfg.crops = cfg.crops
      .map((x) => ({
        name: String(x?.name || "").trim(),
        cat: String(x?.cat || "General").trim(),
        tier: Number.isFinite(+x?.tier) ? +x.tier : 0
      }))
      .filter((x) => x.name);

    cfg.vouchers = Array.isArray(cfg.vouchers) ? cfg.vouchers : [];
    cfg.vouchers = cfg.vouchers
      .map((v) => ({
        code: normalizeCode(v?.code || ""),
        amount: Number.isFinite(+v?.amount) ? +v.amount : (Number.isFinite(+v?.value) ? +v.value : 0)
      }))
      .filter((v) => v.code && v.amount > 0);

    return cfg;
  }

  // ---------- OUTPUT CLEAN / SANITIZE ----------
  function decodeIfEscaped(html) {
    const s = String(html || "");
    if (s.includes("&lt;") && (s.includes("&lt;div") || s.includes("&lt;table") || s.includes("&lt;h"))) {
      const ta = document.createElement("textarea");
      ta.innerHTML = s;
      return ta.value;
    }
    return s;
  }

  function stripCSSLikeText(s) {
    let out = String(s || "");
    out = out.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "");
    const cssBlock = /(^|\s)(body|p|h[1-6]|ul|ol|li|table|thead|tbody|tr|th|td|div|span|a)\s*\{[^}]*\}/gi;
    out = out.replace(cssBlock, " ");
    out = out.replace(/(\s*[.#]?[a-zA-Z0-9_-]+\s*\{[^}]*\}\s*){2,}/g, " ");
    out = out.replace(/(^|\s)(font-family|line-height|color|margin|padding|background|border|box-shadow)\s*:\s*[^;]+;?/gi, " ");
    out = out.replace(/\s{2,}/g, " ").trim();
    return out;
  }

  function extractHtmlOnly(raw) {
    let s = String(raw || "");
    s = s.replace(/```[\s\S]*?```/g, "");
    s = s.replace(/```/g, "");

    const first = s.search(/<(div|h[1-4]|p|ul|ol|li|table|thead|tbody|tr|th|td|a)\b/i);
    if (first >= 0) s = s.slice(first);

    const hasTag = /<(div|h[1-4]|p|ul|ol|li|table|tr|th|td|a)\b/i.test(s);
    if (!hasTag) {
      const safe = escapeHTML(s).replace(/\n{2,}/g, "<br><br>").replace(/\n/g, "<br>");
      return `<div><p>${safe}</p></div>`;
    }
    return s;
  }

  function sanitizeToSafeHTML(input) {
    const allowedTags = new Set([
      "A","B","STRONG","I","EM","U","P","BR",
      "UL","OL","LI",
      "TABLE","THEAD","TBODY","TR","TH","TD",
      "H1","H2","H3","H4",
      "CODE","PRE",
      "SPAN","DIV"
    ]);

    const allowedAttrs = { A: new Set(["href","target","rel"]) };

    const tpl = document.createElement("template");
    tpl.innerHTML = String(input || "");

    const walk = (node) => {
      const kids = Array.from(node.childNodes);
      for (const k of kids) {
        if (k.nodeType === 8) { k.remove(); continue; }
        if (k.nodeType === 1) {
          const tag = k.tagName.toUpperCase();

          if (!allowedTags.has(tag)) {
            const t = document.createTextNode(k.textContent || "");
            k.replaceWith(t);
            continue;
          }

          for (const a of Array.from(k.attributes || [])) {
            const name = a.name.toLowerCase();
            if (name.startsWith("on")) { k.removeAttribute(a.name); continue; }
            if (tag === "A") {
              if (!allowedAttrs.A.has(a.name)) k.removeAttribute(a.name);
            } else {
              k.removeAttribute(a.name);
            }
          }

          if (tag === "A") {
            const href = k.getAttribute("href") || "";
            const safe = /^(https?:\/\/|\/)/i.test(href);
            if (!safe) k.removeAttribute("href");
            k.setAttribute("rel", "noopener noreferrer");
            if (!k.getAttribute("target")) k.setAttribute("target", "_blank");
          }

          walk(k);
        }
      }
    };

    walk(tpl.content);
    return tpl.innerHTML;
  }

  function applyOutputReadability(outEl) {
    if (!outEl) return;

    outEl.querySelectorAll("table").forEach((t) => {
      t.style.width = "100%";
      t.style.borderCollapse = "collapse";
      t.style.margin = "10px 0";
    });
    outEl.querySelectorAll("th,td").forEach((c) => {
      c.style.border = "1px solid rgba(2,44,16,.10)";
      c.style.padding = "8px";
      c.style.verticalAlign = "top";
    });
    outEl.querySelectorAll("th").forEach((th) => {
      th.style.background = "rgba(31,122,59,.08)";
      th.style.color = "#166534";
      th.style.fontWeight = "900";
    });
    outEl.querySelectorAll("h3").forEach((h) => {
      h.style.margin = "12px 0 8px";
      h.style.color = "#166534";
    });
    outEl.querySelectorAll("p,li").forEach((x) => {
      x.style.lineHeight = "1.55";
    });
  }

  function setOutLoading(outEl, on, msg = "") {
    if (!outEl) return;

    if (!on) {
      const l = outEl.querySelector("[data-hg-out-loader]");
      if (l) l.remove();
      return;
    }

    const loader = document.createElement("div");
    loader.setAttribute("data-hg-out-loader", "1");
    loader.style.border = "1px solid rgba(2,44,16,.10)";
    loader.style.background = "linear-gradient(180deg, rgba(31,122,59,.06), rgba(255,255,255,.92))";
    loader.style.borderRadius = "16px";
    loader.style.padding = "14px";
    loader.style.margin = "0 0 12px";
    loader.style.display = "flex";
    loader.style.alignItems = "center";
    loader.style.gap = "10px";
    loader.style.fontWeight = "950";

    loader.innerHTML = `
      <span style="font-size:20px;">⏳</span>
      <span>${escapeHTML(msg || "Se procesează…")} <span data-hg-dots style="display:inline-block; min-width:18px;">…</span></span>
    `;

    outEl.prepend(loader);

    const dots = loader.querySelector("[data-hg-dots]");
    if (dots) {
      let i = 0;
      const t = setInterval(() => {
        if (!document.body.contains(loader)) { clearInterval(t); return; }
        i = (i + 1) % 4;
        dots.textContent = ".".repeat(i) || " ";
      }, 350);
    }
  }

  // ---------- STORAGE ----------
  const LS = {
    guestInit: "hg_guest_init_v92",
    guestTok: "hg_tk_v92",
    usedVouchers: "hg_used_v92",
    memberWelcomeAppliedPrefix: "hg_member_welcome_applied_v92_",
    premiumWelcomeAppliedPrefix: "hg_premium_welcome_applied_v92_"
  };
  function tokKeyForUser(email) {
  const e = String(email || "").trim().toLowerCase();
  return e ? `hg_used_v92_${e}` : LS.usedVouchers; // fallback guest
}

function getUsedVouchers(email) {
  try {
    return JSON.parse(localStorage.getItem(tokKeyForUser(email)) || "[]");
  } catch (_) {
    return [];
  }
}

function setUsedVouchers(email, arr) {
  try {
    localStorage.setItem(tokKeyForUser(email), JSON.stringify(Array.isArray(arr) ? arr : []));
  } catch (_) {}
}

function markVoucherUsed(email, code) {
  const c = normalizeCode(code);
  if (!c) return;
  const used = getUsedVouchers(email);
  if (!used.includes(c)) {
    used.push(c);
    setUsedVouchers(email, used);
  }
}

function voucherAlreadyUsed(email, code) {
  const c = normalizeCode(code);
  if (!c) return false;
  return getUsedVouchers(email).includes(c);
}

function updateTokUI(root, tok) {
  try {
    const iso = root?.querySelector("#hg-iso");
    const el = iso?.querySelector("#hgTok");
    if (el) el.textContent = String(Math.max(0, parseInt(tok || 0, 10)));
  } catch (_) {}
  try {
    document.dispatchEvent(new CustomEvent("hg:tokens-updated", { detail: { tok: tok } }));
  } catch (_) {}
}


  function computeUserTier(status) {
    if (status === "premium") return 2;
    if (status === "member") return 1;
    return 0;
  }

  // ---------- SINGLE INSTANCE GUARD ----------
  let CORE_RUNNING = false;
  let CORE_INSTANCE_ID = 0;

  async function bootCore() {
    const root = findRoot();
    if (!root) return;

    CORE_INSTANCE_ID++;
    const myId = CORE_INSTANCE_ID;
    if (CORE_RUNNING) return;
    CORE_RUNNING = true;

    const BOOT = parseBoot(root);

    window.HG_ENV = {
      user: (BOOT && BOOT.user) || {},
      config: (BOOT && BOOT.config) || {},
      api: (BOOT && BOOT.api) || {},
      products: Array.isArray(BOOT?.products) ? BOOT.products : []
    };

    const API_BASE = String((window.HG_ENV.api && window.HG_ENV.api.base) || "https://chatbot.hortigo.ro/api").replace(/\/+$/, "");
    const API_AI   = String((window.HG_ENV.api && window.HG_ENV.api.ai)   || "https://chatbot.hortigo.ro/v1/prompt-create").replace(/\/+$/, "");

    const state = {
      root,
      boot: BOOT,
      env: window.HG_ENV,
      api: { base: API_BASE, ai: API_AI },
      user: {
        logged: !!(BOOT.user && BOOT.user.logged),
        email: String((BOOT.user && BOOT.user.email) || "").trim(),
        status: String((BOOT.user && BOOT.user.status) || "guest"),
        pending: !!(BOOT.user && BOOT.user.pending),
        tier: 0,
        tok: 0
      },
      cfg: clone(DEFAULT_CFG),
      uiTier: {} // per module selection
    };

    state.user.tier = computeUserTier(state.user.status);

    async function api(path, body = null) {
      const url = API_BASE + path;
      const opts = {
        method: body ? "POST" : "GET",
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined
      };
      const r = await fetch(url, opts);
      return await r.json();
    }

    function emit(name, detail) {
      try { document.dispatchEvent(new CustomEvent(name, { detail })); } catch (_) {}
    }

    function getCosts() {
      const c = state.cfg.costs || {};
      return {
        basic: Number.isFinite(+c.basic) ? +c.basic : 1,
        std:   Number.isFinite(+c.std)   ? +c.std   : 3,
        prem:  Number.isFinite(+c.prem)  ? +c.prem  : 5
      };
    }

    function initGuestCredits() {
      const inited = localStorage.getItem(LS.guestInit);
      if (!inited) {
        localStorage.setItem(LS.guestInit, "1");
        localStorage.setItem(LS.guestTok, "2");
      }
      const t = parseInt(localStorage.getItem(LS.guestTok) || "0", 10);
      state.user.tok = Number.isFinite(t) ? t : 0;
      emit("hg:tokens-updated", { tokens: state.user.tok });
    }

    async function loadServerTokens() {
      if (!state.user.logged || !state.user.email) return;
      try {
        const d = await api("/get-user", { email: state.user.email });
        if (d && d.success && Number.isFinite(+d.tokens)) {
          state.user.tok = +d.tokens;
          emit("hg:tokens-updated", { tokens: state.user.tok });
        }
      } catch (_) {}
    }

    function emailKeySafe() {
      return String(state.user.email || "").trim().toLowerCase();
    }

    async function ensureWelcomeGrantServerSynced() {
      if (!state.user.logged || !state.user.email) return;

      const key = emailKeySafe();
      if (!key) return;

      const PREMIUM_CODE = String((BOOT.config && BOOT.config.autoTopupCode) || "").trim() || "WELCOME_PREMIUM_50";
      const MEMBER_CODE  = "WELCOME_MEMBER_10";

      const want = (state.user.tier === 2) ? 50 : (state.user.tier === 1 ? 10 : 0);
      const code = (state.user.tier === 2) ? PREMIUM_CODE : (state.user.tier === 1 ? MEMBER_CODE : "");
      if (!want || !code) return;

      const appliedKey = (state.user.tier === 2)
        ? (LS.premiumWelcomeAppliedPrefix + key)
        : (LS.memberWelcomeAppliedPrefix + key);

      if (localStorage.getItem(appliedKey) === "1") return;

      const before = Number.isFinite(+state.user.tok) ? +state.user.tok : 0;
      if (before >= want) {
        localStorage.setItem(appliedKey, "1");
        return;
      }

      try {
        const r = await api("/redeem", { email: state.user.email, code });
        await loadServerTokens();
        const after = Number.isFinite(+state.user.tok) ? +state.user.tok : 0;

        if ((r && r.success) && (after > before || after >= want)) {
          localStorage.setItem(appliedKey, "1");
        }
      } catch (_) {}
    }

    async function consume(cost) {
      cost = parseInt(cost || 0, 10);
      if (!Number.isFinite(cost) || cost <= 0) return true;

      if (state.user.tok < cost) return false;

      state.user.tok -= cost;

if (state.user.logged) {
  try {
    const r = await api("/consume", { email: state.user.email, cost });
    if (r && r.success && Number.isFinite(+r.tokens)) state.user.tok = +r.tokens;
  } catch (_) {}
} else {
  try { localStorage.setItem(LS.guestTok, String(state.user.tok)); } catch (_) {}
}

// IMPORTANT: UI refresh
updateTokUI(state.root, state.user.tok);

return true;

    }

   async function redeem(codeRaw) {
  const code = normalizeCode(codeRaw);
  if (!code) return { success: false, message: "Cod lipsă." };

  // 1) blocăm reutilizarea local (și pentru user logat)
  if (voucherAlreadyUsed(state.user.email, code)) {
    return { success: false, message: "Cod deja folosit!" };
  }

  // 2) găsim voucher în config (pentru fallback / pentru guest)
  const localVoucher = (state.cfg.vouchers || []).find(v => normalizeCode(v.code) === code);
  const localAdd = parseInt(localVoucher?.amount || 0, 10);

  // --- GUEST: aplicare locală ---
  if (!state.user.logged) {
    if (!localVoucher) return { success: false, message: "Cod invalid." };
    const add = Number.isFinite(localAdd) ? localAdd : 0;

    state.user.tok += add;
    try { localStorage.setItem(LS.guestTok, String(state.user.tok)); } catch (_) {}

    markVoucherUsed(state.user.email, code);
    updateTokUI(state.root, state.user.tok);

    return { success: true, added: add, tokens: state.user.tok };
  }

  // --- LOGGED: încercăm server, dar cu fallback solid ---
  try {
    const d = await api("/redeem", { email: state.user.email, code });

    // dacă serverul chiar returnează tokens, super
    if (d && d.success) {
      // 1) dacă serverul dă tokens, le folosim
      if (Number.isFinite(+d.tokens)) {
        state.user.tok = +d.tokens;
      } else {
        // 2) altfel măcar adăugăm added local, ca să fie utilizabil imediat
        const added = Number.isFinite(+d.added) ? +d.added : (Number.isFinite(localAdd) ? localAdd : 0);
        state.user.tok = (Number.isFinite(+state.user.tok) ? +state.user.tok : 0) + added;
      }

      // marchează folosit local (în caz că serverul nu blochează)
      markVoucherUsed(state.user.email, code);

      // refresh UI
      updateTokUI(state.root, state.user.tok);

      return { success: true, added: d.added, tokens: state.user.tok };
    }

    // server zice fail => încercăm local dacă există
    if (localVoucher && Number.isFinite(localAdd) && localAdd > 0) {
      state.user.tok = (Number.isFinite(+state.user.tok) ? +state.user.tok : 0) + localAdd;
      markVoucherUsed(state.user.email, code);
      updateTokUI(state.root, state.user.tok);
      return { success: true, added: localAdd, tokens: state.user.tok, message: "Aplicat local (fallback)." };
    }

    return { success: false, message: (d && d.message) || "Cod invalid / deja folosit." };
  } catch (_) {
    // VPS picat => fallback local
    if (localVoucher && Number.isFinite(localAdd) && localAdd > 0) {
      state.user.tok = (Number.isFinite(+state.user.tok) ? +state.user.tok : 0) + localAdd;
      markVoucherUsed(state.user.email, code);
      updateTokUI(state.root, state.user.tok);
      return { success: true, added: localAdd, tokens: state.user.tok, message: "Aplicat local (VPS offline)." };
    }
    return { success: false, message: "Eroare server." };
  }
}

    async function ensureConfig() {
      try {
        const d = await api("/get-config");
        if (myId !== CORE_INSTANCE_ID) return state.cfg;

        if (d && (d.costs || d.packages || Array.isArray(d.crops) || Array.isArray(d.vouchers))) {
          state.cfg = normalizeConfig(d);
        } else {
          state.cfg = normalizeConfig(state.cfg);
        }
      } catch (_) {
        state.cfg = normalizeConfig(state.cfg);
      }

      emit("hg:config-updated", { cfg: state.cfg });
      return state.cfg;
    }

    async function runPrompt({ prompt, type = "TEXT", file = null } = {}) {
      // IMPORTANT: endpoint AI = BOOT.api.ai (FormData)
      const fd = new FormData();
      fd.append("prompt", String(prompt || ""));
      fd.append("type", type === "TEXT_IMAGE" ? "TEXT_IMAGE" : "TEXT");
      if (type === "TEXT_IMAGE" && file) fd.append("file", file);

      const r = await fetch(API_AI, { method: "POST", body: fd });
      const d = await r.json();

      let raw = d?.result || d?.output || "";
      raw = decodeIfEscaped(raw);
      raw = extractHtmlOnly(raw);
      raw = stripCSSLikeText(raw);
      const safe = sanitizeToSafeHTML(raw);

      return { raw, safe, payload: d };
    }

    // Legacy alias used by old modules (expects string)
    async function runAI({ prompt, type = "TEXT", file = null } = {}) {
      const r = await runPrompt({ prompt, type, file });
      return r.safe || "";
    }

    // Prompt helpers (so modules don’t crash)
    function buildPromptMarket({ tier = "basic", crop = "" } = {}) {
      const lvl = tier === "prem" ? "premium" : tier === "std" ? "standard" : "basic";
      return `
Ești broker agricol pentru România.
Generează răspuns DOAR HTML (fără markdown).
Cultură: ${crop}
Nivel: ${lvl}

Vreau:
- tabel cu: canal vânzare, preț orientativ, cerere, risc, recomandare
- 5 bulleturi: strategie negociere + logistică + timing
- concluzie scurtă

IMPORTANT: Fără CSS, fără <style>, fără script.
      `.trim();
    }

    function buildPromptChat({ text = "", context = "" } = {}) {
      return `
Răspunde DOAR în HTML (fără markdown).
Context: ${context}
Întrebare utilizator: ${text}

Fă răspuns clar, structurat, cu liste/tabel unde ajută. Fără CSS.
      `.trim();
    }

    function buildPromptDoctor({ crop = "", symptoms = "", tier = "basic" } = {}) {
      const lvl = tier === "prem" ? "premium" : tier === "std" ? "standard" : "basic";
      return `
Ești agronom fitopatolog.
Răspuns DOAR HTML.
Cultură: ${crop}
Nivel: ${lvl}
Simptome: ${symptoms}

Vreau:
- 3 ipoteze (probabilitate)
- ce verifici în teren
- tratament/prevenție (respectând bune practici)
Fără CSS.
      `.trim();
    }

    // ---------- NAV ----------
    function navTo(viewKey) {
      const iso = state.root.querySelector("#hg-iso");
      if (!iso) return;

      const views = Array.from(iso.querySelectorAll(".hg-view"));
      const navBtns = Array.from(iso.querySelectorAll(".hg-nav"));

      views.forEach(v => v.classList.remove("is-active"));
      navBtns.forEach(b => b.classList.remove("is-active"));

      const view = iso.querySelector(`#hgView-${CSS.escape(String(viewKey || ""))}`);
      if (view) view.classList.add("is-active");

      const btn = navBtns.find(b => b.getAttribute("data-view") === String(viewKey || ""));
      if (btn) btn.classList.add("is-active");
    }

    function bindNavOnce() {
      const iso = state.root.querySelector("#hg-iso");
      if (!iso) return;

      if (iso.getAttribute("data-hg-nav-bound") === "1") return;
      iso.setAttribute("data-hg-nav-bound", "1");

      const navBtns = Array.from(iso.querySelectorAll(".hg-nav"));
      navBtns.forEach((b) => b.addEventListener("click", () => navTo(b.getAttribute("data-view"))));

      const walletBtn = iso.querySelector("#hgWalletBtn");
      if (walletBtn) walletBtn.addEventListener("click", () => navTo("wall"));
    }

    // ---------- INIT ----------
    try {
      bindNavOnce();
      await ensureConfig();

      if (state.user.logged) {
        await loadServerTokens();
        await ensureWelcomeGrantServerSynced();
        await loadServerTokens();
        updateTokUI(state.root, state.user.tok);
      } else {
        initGuestCredits();
      }
    } catch (e) {
      console.error("[HG CORE] init crashed:", e);
    } finally {
      // publish
      window.HG = {
        env: state.env,
        state,
        api,
        ensureConfig,
        getCosts,
        loadServerTokens,
        ensureWelcomeGrantServerSynced,
        consume,
        redeem,
        runPrompt,
        runAI,                // ✅ legacy
        buildPromptMarket,    // ✅ legacy
        buildPromptChat,      // ✅ legacy
        buildPromptDoctor,    // ✅ legacy
        navTo,
        ui: {
          escapeHTML,
          setOutLoading,
          applyOutputReadability,
          decodeIfEscaped,
          stripCSSLikeText,
          extractHtmlOnly,
          sanitizeToSafeHTML,
          sanitizeAIHtml: sanitizeToSafeHTML // ✅ legacy
        }
      };

      // ✅ notify modules (market/doctor/wallet/admin/devizier etc)
      try { document.dispatchEvent(new CustomEvent("hg:ready")); } catch (_) {}

      CORE_RUNNING = false;
    }
  }

  function bootSafe() {
    const r = findRoot();
    if (!r) return;
    bootCore();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootSafe);
  } else {
    bootSafe();
  }

  document.addEventListener("shopify:section:load", () => {
    try { bootSafe(); } catch (_) {}
  });
})();
